import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { track, identifyUser } from '../../lib/mixpanel';
import { sendLeadToCapi } from '../../lib/metaCapi';
import CircularProgress from '@mui/material/CircularProgress';
import { ReactComponent as Logo } from '../../svg/newlogo.svg';
import { ReactComponent as PhoneSvg } from '../../svg/phonesvg.svg';
import { ReactComponent as SpeechBubble } from '../../svg/speech11bubble.svg';
import styles from '../../styles/sunbaebriefingpc.module.css';

const ChevronDown = () => (
  <svg className={styles.fieldChevron} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 7.5L10 12.5L15 7.5" stroke="#333d4b" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export default function SunbaeBriefingPc() {
  const navigate = useNavigate();

  const [attendanceType, setAttendanceType] = useState('');
  const [gender, setGender] = useState('');
  const [name, setName] = useState('');
  const [phoneMiddle, setPhoneMiddle] = useState('');
  const [phoneLast, setPhoneLast] = useState('');
  const [enrollmentType, setEnrollmentType] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFixedBar, setShowFixedBar] = useState(true);
  const phoneLastRef = useRef<HTMLInputElement>(null);
  const infoCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (infoCardRef.current) {
        const rect = infoCardRef.current.getBoundingClientRect();
        setShowFixedBar(rect.bottom > 0);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isFormValid =
    attendanceType !== '' &&
    gender !== '' &&
    name.trim() !== '' &&
    phoneMiddle.length === 4 &&
    phoneLast.length === 4 &&
    enrollmentType !== '';

  // 블로그 → peetsunbae.com → 여기 경유 시 fbc/fbp attribution 복원
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fbc = params.get('fbc');
    const fbp = params.get('fbp');
    if (fbc && fbc.length > 0) {
      document.cookie = `_fbc=${fbc}; domain=.suneungsunbae.com; path=/; max-age=7776000; secure; samesite=lax`;
    }
    if (fbp && fbp.length > 0) {
      document.cookie = `_fbp=${fbp}; domain=.suneungsunbae.com; path=/; max-age=7776000; secure; samesite=lax`;
    }
  }, []);

  useEffect(() => {
    track('Waiting List Page Viewed', { page_type: 'briefing_centum' });
  }, []);

  const submit = () => {
    const telephoneNumber = '010' + phoneMiddle + phoneLast;

    setLoading(true);

    const data = {
      location: 'centum',
      select: gender,
      select2: enrollmentType,
      name,
      telephoneNumber,
      cert: '111111',
      etc: attendanceType,
      year: 2026,
      type1: 'winter26',
    };

    fetch('https://peetsunbae.com/waiting/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data }),
    })
      .then((res: any) => res.json())
      .then((result: any) => {
        setLoading(false);

        if (result.message === 'fail' || result.message === 'error') {
          track('Waiting List Submit Failed', {
            reason: result.message,
            location: 'centum',
            form_type: 'briefing_centum',
          });
          alert('오류 관리자 문의(010-9880-0489)');
          return;
        }

        if (result.message === 'success') {
          identifyUser(telephoneNumber, {
            $name: name,
            $phone: telephoneNumber,
            waiting_list_location: 'centum',
            student_type: enrollmentType,
            gender,
            attendance_type: attendanceType,
            waiting_list_date: new Date().toISOString(),
          });
          track('Waiting List Submitted', {
            location: 'centum',
            gender,
            attendance_type: attendanceType,
            student_type: enrollmentType,
            form_type: 'briefing_centum',
          });
          const eventId = `lead_briefing_${telephoneNumber}_${Date.now()}`;
          if ((window as any).fbq) {
            (window as any).fbq(
              'track',
              'Lead',
              {
                content_name: 'briefing_request',
                source: 'briefing',
                value: 100000,
                currency: 'KRW',
              },
              { eventID: eventId }
            );
          }
          sendLeadToCapi({
            eventId,
            phone: telephoneNumber,
            name,
            location: 'centum',
            contentName: 'briefing_request',
          });
          navigate('/registerwaitingfinish', { state: { id: result.id } });
        }
      });
  };

  return (
    <>
    <div className={styles.page}>

      {/* Hero */}
      <section className={styles.heroSection}>
        <div className={styles.heroTitleGroup}>
          <p className={styles.heroTitle}>{'OFF THE RECORD\n수능선배 센텀점 설명회'}</p>
          <p className={styles.heroSubtitle}>최상위권을 향한 여정, 대치동 1등 독학재수학원 수능선배가 함께합니다</p>
        </div>
        <div className={styles.divider} />
      </section>

      {/* Benefits */}
      <section className={styles.benefitsSection}>
        <div className={styles.benefitsLabel}>
          <p className={styles.benefitsLabelText}>{'참여자 전원에게 드리는\n한정 혜택'}</p>
        </div>

        <div className={styles.benefitsList}>
          <div className={styles.divider} />

          <div className={styles.benefitItem}>
            <div className={styles.benefitTextCol}>
              <p className={styles.benefitItemTitle}>{'Medical 296 Databook \n8개 유형으로 분류한\n합격 멘토 296인의 점수·진도·노하우'}</p>
              <div className={styles.logoRow}>
                {[1,2,3,4,5,6,7,8].map(n => (
                  <div key={n} className={styles.logoIcon}>
                    <img src={`/img/briefing/u${n}_pc.png`} alt="" />
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.imagePlaceholder}>
              <img src="/img/briefing/pc1.webp" alt="" />
            </div>
          </div>
          <div className={styles.divider} />

          <div className={styles.benefitItem}>
            <div className={styles.benefitTextCol}>
              <p className={styles.benefitItemTitle}>{'수능선배 센텀점\n등록비 지원'}</p>
            </div>
            <div className={styles.imagePlaceholder}>
              <img src="/img/briefing/pc2.webp" alt="" />
            </div>
          </div>
          <div className={styles.divider} />

          <div className={styles.benefitItem}>
            <div className={styles.benefitTextCol}>
              <p className={styles.benefitItemTitle}>{'지정좌석\n우선선택권'}</p>
            </div>
            <div className={styles.imagePlaceholder}>
              <img src="/img/briefing/pc3.webp" alt="" />
            </div>
          </div>
          <div className={styles.divider} />
        </div>
      </section>

      {/* Reservation */}
      <section className={styles.reservationSection}>
        <p className={styles.reservationTitle}>설명회 예약하기</p>

        <div className={styles.infoCard} ref={infoCardRef}>
          <p className={styles.infoCardTitle}>수능선배 센텀점 설명회</p>
          <div className={styles.divider} />
          <div className={styles.infoList}>
            <div className={styles.infoRow}>
              <ul style={{ margin: 0, paddingLeft: 30 }}>
                <li><span>일시</span></li>
              </ul>
              <p className={styles.infoValue}>2026년 06월 06일 (토)</p>
            </div>
            <div className={styles.infoRow}>
              <ul style={{ margin: 0, paddingLeft: 30 }}>
                <li><span>대상</span></li>
              </ul>
              <p className={styles.infoValue}>재학생/N수생</p>
            </div>
            <div className={styles.infoRow}>
              <ul style={{ margin: 0, paddingLeft: 30 }}>
                <li><span>장소</span></li>
              </ul>
              <p className={styles.infoValue}>수능선배 센텀점 (부산광역시 해운대구 센텀2로 29 메커스빌딩 8층)</p>
            </div>
          </div>
          <div className={styles.divider} />
          <p className={styles.infoDisclaimer}>
            예약은 선착순으로 진행되며, 예약 상황에 따라 조기 마감될 수 있습니다.<br />
            등록된 연락처로 설명회 안내 문자를 발송해 드리오니, 정확하게 입력해 주시기 바랍니다.
          </p>
        </div>

        <div className={styles.form}>
          <div className={styles.formFields}>

            <div className={styles.formRow}>
              <p className={styles.formLabel}>참석 구분</p>
              <div className={styles.fieldWrapper}>
                <select
                  className={!attendanceType ? styles.placeholder : ''}
                  value={attendanceType}
                  onChange={e => setAttendanceType(e.target.value)}
                >
                  <option value="" disabled>참석 구분을 선택해주세요</option>
                  <option value="attend">설명회 참석</option>
                  <option value="sms">안내 문자 수신</option>
                </select>
                <ChevronDown />
              </div>
            </div>

            <div className={styles.formRow}>
              <p className={styles.formLabel}>학생 성별</p>
              <div className={styles.fieldWrapper}>
                <select
                  className={!gender ? styles.placeholder : ''}
                  value={gender}
                  onChange={e => setGender(e.target.value)}
                >
                  <option value="" disabled>학생 성별을 선택해주세요</option>
                  <option value="male">남학생</option>
                  <option value="female">여학생</option>
                </select>
                <ChevronDown />
              </div>
            </div>

            <div className={styles.formRow}>
              <p className={styles.formLabel}>성명</p>
              <input
                className={styles.textInput}
                type="text"
                placeholder="성명을 입력해 주세요"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>

            <div className={styles.formRow}>
              <p className={styles.formLabel}>연락처</p>
              <div className={styles.fieldWrapper}>
                <select value="010" onChange={() => {}}>
                  <option value="010">010</option>
                </select>
                <ChevronDown />
              </div>
              <input
                className={styles.phoneInput}
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={phoneMiddle}
                onChange={e => {
                  const val = e.target.value.replace(/\D/g, '');
                  setPhoneMiddle(val);
                  if (val.length === 4) phoneLastRef.current?.focus();
                }}
              />
              <input
                ref={phoneLastRef}
                className={styles.phoneInput}
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={phoneLast}
                onChange={e => setPhoneLast(e.target.value.replace(/\D/g, ''))}
              />
            </div>

            <div className={styles.formRow}>
              <p className={styles.formLabel}>재학 구분</p>
              <div className={styles.fieldWrapper}>
                <select
                  className={!enrollmentType ? styles.placeholder : ''}
                  value={enrollmentType}
                  onChange={e => setEnrollmentType(e.target.value)}
                >
                  <option value="" disabled>재학 구분을 선택해주세요</option>
                  <option value="current">재학생</option>
                  <option value="n">N수생</option>
                </select>
                <ChevronDown />
              </div>
            </div>

          </div>

          <button
            className={styles.submitBtn}
            type="button"
            disabled={!isFormValid || loading}
            onClick={submit}
          >
            {loading
              ? <CircularProgress color="inherit" size={24} />
              : <span className={styles.submitBtnText}>예약하기</span>
            }
          </button>
        </div>
      </section>

    </div>

    <div className={styles.fixedDiv} style={{ display: showFixedBar ? 'block' : 'none' }}>
      <div className={styles.fixedDivInner}>
        <div className={styles.fixedDivInner1}>
          <Logo className={styles.fixedDivLogoImg} />
          <div className={styles.fixedDivVerticalLine} />
          <div className={styles.fixedDivText}>
            대치동 1등 독학재수학원<br />수능선배 센텀점 설명회 신청
          </div>
        </div>
        <div className={styles.fixedDivInner2}>
          <button
            className={styles.fixedDivBtn1}
            onClick={() => window.open('https://blog.naver.com/bioochemistry/224275550968', '_blank')}
          >
            센텀점 소개
          </button>
          <button
            className={styles.fixedDivBtn2}
            onClick={() => { window.location.href = 'tel://16685786'; }}
          >
            <PhoneSvg className={styles.fixedDivBtn2Img} />
          </button>
          <button
            className={styles.fixedDivBtn2}
            onClick={() => window.open('http://pf.kakao.com/_Whxexgxj/chat', '_blank')}
          >
            <SpeechBubble className={styles.fixedDivBtn2Img} />
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
